// C:\Projects\texe\texe-web\src\pages\AdminUsernames.tsx
import { useEffect, useMemo, useState } from "react";
import { apiDelete, apiGet, apiPost } from "../lib/api";
import { useAuth } from "../context/AuthProvider";

type Reservation = {
  name: string;
  until: string;                               // ISO string
  reason: "rename" | "soft_delete" | "manual";
  byUserId: string | null;
  createdAt?: string;
  updatedAt?: string;
};

type ListResp = { ok: true; items: Reservation[] };

// Modern card styles
const cardStyle: React.CSSProperties = {
  background: "#0a0a0a",
  border: "1px solid #1a1a1a",
  borderRadius: 12,
  padding: 24,
  marginBottom: 24,
};

const inputStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "#000",
  border: "1px solid #1a1a1a",
  borderRadius: 8,
  color: "#fff",
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  transition: "border-color 0.2s",
};

const buttonStyle: React.CSSProperties = {
  padding: "12px 24px",
  background: "#114232",
  border: "none",
  borderRadius: 8,
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  transition: "background 0.2s",
};

const dangerButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: "#8B0000",
  padding: "8px 16px",
  fontSize: 13,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: 8,
  fontSize: 13,
  color: "#888",
  fontWeight: 500,
};

export default function AdminUsernames() {
  const { user } = useAuth(); // sadece üst bilgi için
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [items, setItems] = useState<Reservation[]>([]);

  // Rezerve ekleme/güncelle formu
  const [newName, setNewName] = useState("");
  const [newUntil, setNewUntil] = useState<string>(""); // yyyy-mm-dd
  const [newReason, setNewReason] = useState<Reservation["reason"]>("manual");
  const [savingReserve, setSavingReserve] = useState(false);

  // Transfer formu
  const [desired, setDesired] = useState("");
  const [toUserId, setToUserId] = useState("");
  const [fromUserId, setFromUserId] = useState("");
  const [savingTransfer, setSavingTransfer] = useState(false);

  // Listeyi aktif/pasif filtrelemek için (opsiyonel)
  const [onlyActive, setOnlyActive] = useState(true);

  const untilPlaceholder = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  async function load() {
    setLoading(true);
    setErr(null);
    setMsg(null);
    try {
      const qs = query ? `?q=${encodeURIComponent(query)}` : "";
      const r = await apiGet<ListResp>(`/api/admin/usernames/reservations${qs}`);
      setItems(r.items || []);
    } catch (e: any) {
      setErr(e?.message || "Liste getirilemedi.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!onlyActive) return items;
    const now = Date.now();
    return items.filter((x) => {
      const t = new Date(x.until).getTime();
      return Number.isFinite(t) && t > now;
    });
  }, [items, onlyActive]);

  async function onAddReserve(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) {
      setErr("Kullanıcı adı gerekli.");
      return;
    }
    setSavingReserve(true);
    setErr(null);
    setMsg(null);
    try {
      const body: any = { name: newName.trim(), reason: newReason };
      if (newUntil) {
        // yyyy-mm-dd → günü kapsasın diye gün sonu:
        body.until = `${newUntil}T23:59:59.000Z`;
      }
      await apiPost(`/api/admin/usernames/reservations`, body);
      setNewName("");
      setNewUntil("");
      setNewReason("manual");
      setMsg("Rezerve eklendi / güncellendi.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Rezerve ekleme başarısız.");
    } finally {
      setSavingReserve(false);
    }
  }

  async function onDeleteReserve(name: string) {
    if (!confirm(`"${name}" rezervasyonunu silmek istiyor musun?`)) return;
    setErr(null);
    setMsg(null);
    try {
      await apiDelete(`/api/admin/usernames/reservations/${encodeURIComponent(name)}`);
      setMsg("Rezerve silindi.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Rezerve silme başarısız.");
    }
  }

  async function onTransfer(e: React.FormEvent) {
    e.preventDefault();
    if (!desired.trim() || !toUserId.trim()) {
      setErr("İstenen username ve hedef kullanıcı ID zorunlu.");
      return;
    }
    setSavingTransfer(true);
    setErr(null);
    setMsg(null);
    try {
      const payload: any = { desired: desired.trim(), toUserId: toUserId.trim() };
      if (fromUserId.trim()) payload.fromUserId = fromUserId.trim();
      const r = await apiPost<{ ok: true; transferred?: boolean; username?: string }>(
        `/api/admin/usernames/transfer`,
        payload
      );
      setMsg(
        r.transferred
          ? `Transfer tamamlandı: @${r.username}`
          : "Hedef kullanıcı zaten bu kullanıcı adına sahip."
      );
      setDesired("");
      setToUserId("");
      setFromUserId("");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Transfer başarısız.");
    } finally {
      setSavingTransfer(false);
    }
  }

  return (
    <div style={{ 
      padding: "40px 24px", 
      maxWidth: 1200, 
      margin: "0 auto",
      background: "#000",
      minHeight: "100vh",
      color: "#fff"
    }}>
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ 
          margin: 0, 
          fontSize: 32, 
          fontWeight: 700,
          marginBottom: 8,
          color: "#fff"
        }}>
          👤 Kullanıcı Adı Yönetimi
        </h1>
        <p style={{ 
          margin: 0, 
          color: "#666",
          fontSize: 14
        }}>
          Admin: <strong style={{ color: "#114232" }}>@{user?.username}</strong>
        </p>
      </div>

      {/* Mesajlar */}
      {msg && (
        <div style={{ 
          background: "rgba(17, 66, 50, 0.2)",
          border: "1px solid #114232",
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          color: "#4ade80",
          fontSize: 14
        }}>
          ✅ {msg}
        </div>
      )}
      {err && (
        <div style={{ 
          background: "rgba(139, 0, 0, 0.2)",
          border: "1px solid #8B0000",
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          color: "#ff6b6b",
          fontSize: 14
        }}>
          ❌ {err}
        </div>
      )}

      {/* Transfer Section - EN ÖNEMLİ ÖZELLİK */}
      <section style={{
        ...cardStyle,
        background: "linear-gradient(135deg, rgba(17, 66, 50, 0.15), rgba(17, 66, 50, 0.05))",
        border: "2px solid #114232",
      }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 12, 
          marginBottom: 16 
        }}>
          <span style={{ fontSize: 24 }}>🔄</span>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: 20, 
              fontWeight: 700,
              color: "#fff"
            }}>
              Kullanıcı Adı Transferi
            </h2>
            <p style={{ 
              margin: "4px 0 0 0", 
              fontSize: 13, 
              color: "#888" 
            }}>
              Bir kullanıcının adını başka bir kullanıcıya zorla aktar
            </p>
          </div>
        </div>

        {/* Örnek Senaryo Kutusu */}
        <div style={{
          background: "rgba(17, 66, 50, 0.1)",
          border: "1px solid rgba(17, 66, 50, 0.3)",
          borderRadius: 8,
          padding: 16,
          marginBottom: 20,
        }}>
          <div style={{ 
            fontSize: 13, 
            fontWeight: 600, 
            color: "#4ade80",
            marginBottom: 8 
          }}>
            📋 Örnek Kullanım Senaryosu:
          </div>
          <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.6 }}>
            <strong style={{ color: "#fff" }}>Durum:</strong> @adidas kullanıcı adı bir kullanıcı tarafından alınmış. 
            Adidas firması resmi hesap açmak istiyor ve bu kullanıcı adını talep ediyor.
            <br /><br />
            <strong style={{ color: "#fff" }}>Çözüm:</strong>
            <br />
            1️⃣ Adidas firması için yeni bir hesap oluştur (veya mevcut hesabın ID'sini al)
            <br />
            2️⃣ Aşağıdaki forma "@adidas" ve yeni hesabın ID'sini gir
            <br />
            3️⃣ Transfer Et butonuna tıkla
            <br />
            4️⃣ <span style={{ color: "#4ade80" }}>Sistem otomatik olarak:</span>
            <br />
            &nbsp;&nbsp;&nbsp;• Mevcut @adidas sahibini güvenli bir isme taşır (örn: adidas_1732349123)
            <br />
            &nbsp;&nbsp;&nbsp;• @adidas kullanıcı adını Adidas firmasının hesabına verir
          </div>
        </div>

        <form onSubmit={onTransfer} style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={labelStyle}>
              İstenen Kullanıcı Adı (@ işareti olmadan)
            </label>
            <input
              value={desired}
              onChange={(e) => setDesired(e.target.value)}
              placeholder="örn: adidas, nike, realmadrid"
              style={{
                ...inputStyle,
                fontSize: 16,
                fontWeight: 600,
              }}
              required
            />
          </div>

          <div>
            <label style={labelStyle}>
              Hedef Kullanıcı ID (Bu ID'ye kullanıcı adı verilecek)
            </label>
            <input
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              placeholder="örn: 6650f9ac1b2c3d4e5f678901"
              style={inputStyle}
              required
            />
            <div style={{ 
              fontSize: 12, 
              color: "#666", 
              marginTop: 6 
            }}>
              💡 Kullanıcı ID'sini Admin → Kullanıcı Yönetimi bölümünden bulabilirsin
            </div>
          </div>

          <div>
            <label style={labelStyle}>
              Mevcut Sahibi ID (Opsiyonel - doğrulama için)
            </label>
            <input
              value={fromUserId}
              onChange={(e) => setFromUserId(e.target.value)}
              placeholder="Boş bırakılabilir"
              style={inputStyle}
            />
          </div>

          <div>
            <button 
              type="submit" 
              disabled={savingTransfer}
              style={{
                ...buttonStyle,
                opacity: savingTransfer ? 0.6 : 1,
                cursor: savingTransfer ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!savingTransfer) e.currentTarget.style.background = "#156b45";
              }}
              onMouseLeave={(e) => {
                if (!savingTransfer) e.currentTarget.style.background = "#114232";
              }}
            >
              {savingTransfer ? "⏳ Aktarılıyor…" : "🔄 Transfer Et"}
            </button>
          </div>
        </form>

        <div style={{ 
          fontSize: 12, 
          color: "#666", 
          marginTop: 16,
          padding: 12,
          background: "rgba(255, 165, 0, 0.05)",
          border: "1px solid rgba(255, 165, 0, 0.2)",
          borderRadius: 6,
        }}>
          ⚠️ <strong style={{ color: "#ffa500" }}>Önemli:</strong> Bu işlem geri alınamaz! 
          Mevcut sahip otomatik olarak yedek bir kullanıcı adına taşınır ve tüm işlem audit log'a kaydedilir.
        </div>
      </section>

      {/* Rezervasyon Section */}
      <section style={cardStyle}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 12, 
          marginBottom: 16 
        }}>
          <span style={{ fontSize: 24 }}>🔒</span>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: 20, 
              fontWeight: 700,
              color: "#fff"
            }}>
              Kullanıcı Adı Rezervasyonu
            </h2>
            <p style={{ 
              margin: "4px 0 0 0", 
              fontSize: 13, 
              color: "#888" 
            }}>
              Belirli kullanıcı adlarını kilitleyerek normal kullanıcıların almasını engelle
            </p>
          </div>
        </div>

        <form onSubmit={onAddReserve} style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={labelStyle}>Kullanıcı Adı</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="örn: founder, admin, nike"
              style={inputStyle}
              maxLength={24}
              required
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <label style={labelStyle}>Bitiş Tarihi (Opsiyonel)</label>
              <input
                type="date"
                value={newUntil}
                onChange={(e) => setNewUntil(e.target.value)}
                placeholder={untilPlaceholder}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Sebep</label>
              <select
                value={newReason}
                onChange={(e) => setNewReason(e.target.value as Reservation["reason"])}
                style={{
                  ...inputStyle,
                  cursor: "pointer",
                }}
              >
                <option value="manual">🔧 Manuel (Admin)</option>
                <option value="rename">✏️ İsim Değişikliği</option>
                <option value="soft_delete">🗑️ Hesap Silme</option>
              </select>
            </div>
          </div>

          <div>
            <button 
              type="submit" 
              disabled={savingReserve}
              style={{
                ...buttonStyle,
                opacity: savingReserve ? 0.6 : 1,
                cursor: savingReserve ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!savingReserve) e.currentTarget.style.background = "#156b45";
              }}
              onMouseLeave={(e) => {
                if (!savingReserve) e.currentTarget.style.background = "#114232";
              }}
            >
              {savingReserve ? "⏳ Kaydediliyor…" : "💾 Kaydet"}
            </button>
          </div>
        </form>
      </section>

      {/* Arama ve Filtre */}
      <div style={{ 
        display: "flex", 
        gap: 12, 
        alignItems: "center", 
        marginBottom: 16,
        flexWrap: "wrap",
      }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="🔍 Rezervasyonlarda ara..."
          style={{
            ...inputStyle,
            flex: 1,
            minWidth: 300,
          }}
        />
        <label style={{ 
          display: "inline-flex", 
          alignItems: "center", 
          gap: 8,
          color: "#aaa",
          fontSize: 14,
          cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          Sadece aktif rezervasyonlar
        </label>
        <button 
          onClick={load} 
          disabled={loading}
          style={{
            ...buttonStyle,
            padding: "12px 20px",
          }}
        >
          {loading ? "⏳" : "🔄"} Yenile
        </button>
      </div>

      {/* Liste */}
      <section style={cardStyle}>
        <div style={{ 
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16 
        }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: 18, 
            fontWeight: 700,
            color: "#fff"
          }}>
            📋 Aktif Rezervasyonlar
          </h2>
          <span style={{ 
            fontSize: 13, 
            color: "#666",
            background: "#1a1a1a",
            padding: "6px 12px",
            borderRadius: 6,
          }}>
            {filtered.length} kayıt
          </span>
        </div>

        {loading ? (
          <div style={{ 
            textAlign: "center", 
            padding: 40, 
            color: "#666" 
          }}>
            ⏳ Yükleniyor...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ 
            textAlign: "center", 
            padding: 40, 
            color: "#666" 
          }}>
            📭 Sonuç bulunamadı
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse",
            }}>
              <thead>
                <tr style={{ 
                  borderBottom: "2px solid #1a1a1a",
                }}>
                  <th style={{ 
                    padding: "12px 8px", 
                    textAlign: "left",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#888",
                  }}>Kullanıcı Adı</th>
                  <th style={{ 
                    padding: "12px 8px", 
                    textAlign: "left",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#888",
                  }}>Bitiş Tarihi</th>
                  <th style={{ 
                    padding: "12px 8px", 
                    textAlign: "left",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#888",
                  }}>Sebep</th>
                  <th style={{ 
                    padding: "12px 8px", 
                    textAlign: "left",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#888",
                  }}>Kullanıcı ID</th>
                  <th style={{ 
                    padding: "12px 8px", 
                    textAlign: "right",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#888",
                  }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const untilTs = new Date(r.until).getTime();
                  const expired = Number.isFinite(untilTs) && untilTs < Date.now();
                  
                  const reasonEmoji = {
                    manual: "🔧",
                    rename: "✏️",
                    soft_delete: "🗑️",
                  }[r.reason];

                  return (
                    <tr 
                      key={r.name} 
                      style={{ 
                        borderBottom: "1px solid #1a1a1a",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#0a0a0a";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <td style={{ 
                        padding: "12px 8px",
                        fontWeight: 600,
                        color: "#4ade80",
                      }}>
                        @{r.name}
                      </td>
                      <td style={{ padding: "12px 8px", color: "#aaa" }}>
                        <span title={new Date(r.until).toLocaleString()}>
                          {new Date(r.until).toLocaleDateString("tr-TR")}
                        </span>
                        {expired && (
                          <span style={{ 
                            marginLeft: 8,
                            fontSize: 11,
                            color: "#ffa500",
                            background: "rgba(255, 165, 0, 0.1)",
                            padding: "2px 6px",
                            borderRadius: 4,
                          }}>
                            Süresi doldu
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "12px 8px", color: "#aaa" }}>
                        {reasonEmoji} {r.reason}
                      </td>
                      <td style={{ 
                        padding: "12px 8px",
                        color: "#666",
                        fontFamily: "monospace",
                        fontSize: 12,
                      }}>
                        {r.byUserId || "-"}
                      </td>
                      <td style={{ 
                        padding: "12px 8px", 
                        textAlign: "right" 
                      }}>
                        <button 
                          onClick={() => onDeleteReserve(r.name)}
                          style={dangerButtonStyle}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#a00";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#8B0000";
                          }}
                        >
                          🗑️ Sil
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
